import { Link, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'

import type { Photos } from '@jotta/types/Photos'
import type { PhotosApi } from '@jotta/types/PhotosApi'
import { CollectionType } from '@jotta/types/schemas/AlbumSchema'
import { useBrandStore } from '@jotta/ui/useBrandTheme'

import { AlbumThumb } from '../AlbumThumb/AlbumThumb'
import { MoreAlbumsThumb } from '../MoreAlbumsThumb/MoreAlbumsThumb'

import { BaseAlbumsSection } from './BaseAlbumsSection'
import { CreateAlbumFromAlbumThumb } from './CreateAlbumFromAlbumThumb'

const albumTypeRouteMap: Record<CollectionType, string> = {
  [CollectionType.NORMAL_ALBUM]: 'all',
  [CollectionType.SHARED_PHOTOS]: 'shared',
  [CollectionType.META_SHARED_ALBUM_TYPES]: 'shared',
  [CollectionType.LOCATION_ALBUM]: 'location',
}

export interface AlbumsSectionProps {
  sectionType?: Photos.DisplayCollectionTypeKey
  showAll?: boolean
  isLoading?: boolean
  title?: string
  albums?: PhotosApi.AlbumInfo[]
  selectedAlbum?: string
  setSelectedAlbum?: (albumId: string) => void
  backPath?: string
  showAvatar?: boolean
}
export const AlbumsSection = observer<AlbumsSectionProps>(
  function AlbumListSection({
    sectionType = 'NORMAL_ALBUM',
    albums = [],
    selectedAlbum = '',
    setSelectedAlbum,
    isLoading = false,
    showAll = false,
    title,
    backPath,
    showAvatar = false,
  }) {
    const navigate = useNavigate()
    const branding = useBrandStore()
    const columns = Math.max(2, Math.round(branding.contentWidthSafe / 230))
    const showCreateAlbumButton = sectionType === 'NORMAL_ALBUM'
    const visible = showAll
      ? albums.length
      : Math.min(albums.length, columns - (showCreateAlbumButton ? 2 : 1))
    const visibleAlbums = albums.slice(0, visible)
    const hiddenAlbums: PhotosApi.AlbumInfo[] = showAll
      ? []
      : albums.slice(visible)
    const albumCollectionType = CollectionType[sectionType]
    const moreAlbumsLink = hiddenAlbums.length > 0 && (
      <Link key="has_more" to={`./${albumTypeRouteMap[albumCollectionType]}`}>
        <MoreAlbumsThumb
          albums={hiddenAlbums}
          sectionType={albumCollectionType}
        />
      </Link>
    )

    if (
      !isLoading &&
      !showCreateAlbumButton &&
      !visibleAlbums.length &&
      !hiddenAlbums.length
    ) {
      return null
    }

    return (
      <BaseAlbumsSection
        columns={columns}
        albumCount={albums.length}
        isLoading={isLoading}
        showAll={showAll}
        title={title}
        albumType={sectionType}
      >
        {showCreateAlbumButton && (
          <CreateAlbumFromAlbumThumb
            onAlbumCreated={album => navigate(`/photo/album/${album.id}`)}
          />
        )}
        {visibleAlbums.map(album =>
          setSelectedAlbum ? (
            <button
              key={album.id}
              type="button"
              onClick={() =>
                setSelectedAlbum(selectedAlbum === album.id ? '' : album.id)
              }
              aria-pressed={selectedAlbum === album.id}
              className="m-0 appearance-none border-0 bg-transparent p-0 text-left text-inherit"
            >
              <AlbumThumb album={album} showAvatar={showAvatar} />
            </button>
          ) : (
            <Link
              to={album.id}
              key={album.id}
              state={backPath ? { backPath } : undefined}
              className="text-inherit no-underline"
            >
              <AlbumThumb album={album} showAvatar={showAvatar} />
            </Link>
          ),
        )}
        {moreAlbumsLink}
      </BaseAlbumsSection>
    )
  },
)
